import {
  Button,
  ColorPicker,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToast,
} from '@ais-ui/ui-core';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { CustomizePopover } from './CustomizePopover';
import { ColorInterval, trackReportMiscellaneous } from '../reportsTypes';
import { ParsedReportParams, useTrackReconfigure } from '@ais-ui/websockets';
import { useFetchOverlayData } from '../useMapReportOverlay';
import { PiMinusThin, PiPlusThin } from 'react-icons/pi';
import { convertBackToColorIntervals } from '../Mutations/utils/parseColorInterval';

interface CustomizeTrackProps {
  report: ParsedReportParams;
  className?: string;
}

export const CustomizeTrack = ({ report, className }: CustomizeTrackProps) => {
  const fetchOverlayData = useFetchOverlayData();
  const { mutate } = useTrackReconfigure(fetchOverlayData);

  const [colorBy, setColorBy] = React.useState(report.params.CB);
  const [colorIntervals, setColorIntervals] = React.useState<ColorInterval[]>(
    convertBackToColorIntervals(JSON.parse(report?.params['CBCD']))
  );

  const { toast } = useToast();

  const onApply = () => {
    const colors = {
      colors: colorIntervals.map((colorInterval: ColorInterval) => ({
        val: null,
        rgb: colorInterval.color,
        sVal: colorInterval.startInterval,
        eVal: colorInterval.endInterval,
      })),
    };

    mutate(
      {
        jobId: report.info?.['Job ID'] as string,
        colorBy: colorBy,
        colorIntervals: colors,
      },
      {
        onSuccess: () => {
          toast({
            title: 'Success!',
            description: 'Customized track report successfully',
          });
        },
        onError: () => {
          toast({
            title: 'Error!',
            description: 'Error customizing track report',
            variant: 'destructive',
          });
        },
      }
    );
  };

  return (
    <CustomizePopover>
      <form className="grid gap-8 p-2 w-96">
        <div className="space-y-2">
          <Label htmlFor="color-by">Color By</Label>
          <Select value={colorBy} onValueChange={setColorBy}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Color by" />
            </SelectTrigger>
            <SelectContent>
              {trackReportMiscellaneous?.colorBy?.options?.map((colorBy) => (
                <SelectItem key={colorBy.value} value={colorBy.value}>
                  {colorBy.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-4">
          <div className="flex flex-col space-y-2">
            <Label htmlFor="minmaxships">Color intervals</Label>
          </div>
          {colorIntervals.map((colorInterval: ColorInterval, index) => (
            <div key={colorInterval.id} className="flex space-x-2">
              <ColorPicker
                background={colorInterval.color}
                setBackground={(color) => {
                  setColorIntervals((prev) => {
                    const updated = [...prev];
                    updated[index].color = color;
                    return updated;
                  });
                }}
                className="flex-shrink-0 flex-grow-0 w-12 p-0"
              />

              <Input
                type="number"
                defaultValue={colorInterval.startInterval}
                onChange={(e) => {
                  const numberValue = e.target.value
                    ? parseFloat(e.target.value)
                    : 0;
                  setColorIntervals((prev) => {
                    const updated = [...prev];
                    updated[index].startInterval = numberValue;
                    return updated;
                  });
                }}
              />
              <Input
                type="number"
                defaultValue={colorInterval.endInterval}
                onChange={(e) => {
                  const numberValue = e.target.value
                    ? parseFloat(e.target.value)
                    : 0;
                  setColorIntervals((prev) => {
                    const updated = [...prev];
                    updated[index].endInterval = numberValue;
                    return updated;
                  });
                }}
              />
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    className="h-9 w-9 flex-shrink-0 flex-grow-0"
                    onClick={() => {
                      setColorIntervals((prev) => {
                        const updated = [...prev];
                        updated.splice(index, 1);
                        return updated;
                      });
                    }}
                  >
                    <PiMinusThin className="h-4 w-4"></PiMinusThin>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Remove interval</TooltipContent>
              </Tooltip>
            </div>
          ))}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                size="icon"
                variant="outline"
                className="h-9 w-9"
                onClick={() => {
                  setColorIntervals((prev) => {
                    const updated = [...prev];
                    updated.push({
                      id: uuidv4(),
                      color: '##003b5c',
                      startInterval: 0,
                      endInterval: 0,
                    });
                    return updated;
                  });
                }}
              >
                <PiPlusThin className="h-4 w-4"></PiPlusThin>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Add interval</TooltipContent>
          </Tooltip>
        </div>
      </form>
      <div className="pt-6 flex justify-end">
        <Button onClick={onApply}>Apply</Button>
      </div>
    </CustomizePopover>
  );
};
